import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Space } from "react_reusable_components";
import { useFormik } from "formik";
import { useIntl } from "react-intl";

import { ProductCategoryContext } from "../../globalContext/productCategory/productCategoryProvider";
import { ThreeRow } from "../../core/layouts";
import CustomInput from "../../components/CustomInput/CustomInput";
import AddSubCategoryFormContainer from "../AddSubCategoryFormContainer";
import ProductCategoryService from "../../services/product-category-service";
import { getLocalizedMessage } from "../../UtilityFunctions/utils";
import { showNotification } from "../../UtilityFunctions/notification";
import {
  AddProductCategoryFormFieldName,
  validationSchema,
} from "../../validationSchema/addProductCategorySchema";
import variables from "../../themes/base/styles/variables";
import Style from "./AddProductCategoryForm.module.scss";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import { getProductCategoryById } from "../../globalContext/productCategory/productCategoryActions";

const AddProductCategoryFormStyle = {
  mainThreeRowStyle: {
    padding: `${variables.paddingMedium} 0`,
    position: "relative",
  },
  topSectionStyle: { padding: `0 ${variables.paddingMedium}` },
  middleSectionStyle: {
    marginTop: variables.marginMediumLarge,
    overflowY: "auto",
    flex: 1,
  },
  bottomSectionStyle: {
    marginTop: variables.marginMediumLarge2,
    display: variables.dFlex,
    justifyContent: "end",
    position: "absolute",
    bottom: variables.paddingMedium,
    right: variables.paddingMedium,
    background: variables.bodyBg,
  },
};

export default function AddProductCategoryFormContainer({ isEditMode }) {
  const [productCategoryState, productCategoryDispatch] = useContext(
    ProductCategoryContext
  );
  const [disabled, setDisabled] = useState(false);
  const loaderDispatch = useContext(GlobalLoaderContext)[1];

  const initialValues = {
    subCategory: [{ name: "", countryId: [] }],
    name: "",
  };

  const navigate = useNavigate();
  const intl = useIntl();
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      ProductCategoryService.getProductCategoryById(
        productCategoryDispatch,
        loaderDispatch,
        id,
        intl
      );
    }
    return () => {
      productCategoryDispatch(getProductCategoryById({}));
    };
  }, [id]);

  useEffect(() => {
    if (isEditMode) {
      setFieldValue(
        AddProductCategoryFormFieldName.NAME,
        productCategoryState?.productCategoryData?.result?.[0]
          ?.productCategoryName
      );
      const newInitialValues =
        productCategoryState?.productCategoryData?.result?.[0]?.subCategory?.map(
          (item) => {
            return {
              ...item,
              countryId: item?.country?.map((country) => country.id),
            };
          }
        );
      setFieldValue(
        AddProductCategoryFormFieldName.SUB_CATEGORY,
        newInitialValues
      );
    }
  }, [productCategoryState]);

  const {
    values,
    handleChange,
    errors,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues: initialValues,
    onSubmit: async (formData) => {
      const updatedBody = {
        ...formData,
      };

      if (isEditMode) {
        updatedBody.productCategoryId = parseInt(id);
      }

      let updatedCategory = formData?.subCategory?.map((item) => {
        if (item?.countryId?.length === 0 && !isEditMode) {
          const { countryId, ...restItems } = item;
          return restItems;
        }
        if (item?.countryId?.length) {
          item.countryId = item.countryId.filter((country) => {
            return country !== "all";
          });
        }

        if (isEditMode) {
          const { country, isActive, id: Id, ...restItem } = item;
          let restKeys = restItem;
          if (item?.countryId?.length === 0) {
            const { countryId, ...rest } = restKeys;
            restKeys = { ...rest };
          }
          if (Id) {
            restKeys = { ...restKeys, subProductCategoryId: parseInt(Id) };
          }
          return restKeys;
        }
        return item;
      });

      updatedBody.subCategory = updatedCategory?.filter(
        (item) => item.hasOwnProperty("name") && item.name
      );
      setDisabled(true);
      if (!isEditMode) {
        await ProductCategoryService.addProductCategory(
          loaderDispatch,
          updatedBody,
          navigate,
          intl,
          resetForm
        );
        setDisabled(false);
      } else {
        await ProductCategoryService.editProductCategory(
          loaderDispatch,
          updatedBody,
          navigate,
          intl,
          resetForm
        );
        setDisabled(false);
      }
    },
    validationSchema: validationSchema(),
  });

  const handleSubProductCategoryChange = (index, field, value) => {
    const newArray = [...values[AddProductCategoryFormFieldName.SUB_CATEGORY]];
    newArray[index][field] = value;
    setFieldValue(AddProductCategoryFormFieldName.SUB_CATEGORY, newArray);
  };

  const addSubProductCategory = () => {
    const container = document.getElementById(`add-category`);
    container.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest",
    });
    setFieldValue(AddProductCategoryFormFieldName.SUB_CATEGORY, [
      ...values[AddProductCategoryFormFieldName.SUB_CATEGORY],
      { countryId: [] },
    ]);
  };

  const removeSubProductCategory = (index) => {
    if (values?.[AddProductCategoryFormFieldName.SUB_CATEGORY]?.length === 1) {
      showNotification(
        "error",
        "Error",
        getLocalizedMessage(intl, "productCategory.requiredSubProductCategory")
      );
      return;
    }
    const newArray = [...values[AddProductCategoryFormFieldName.SUB_CATEGORY]];
    newArray.splice(index, 1);
    setFieldValue([AddProductCategoryFormFieldName.SUB_CATEGORY], newArray);
  };
  const submitAddProductCategory = () => {
    if (!values.name) {
      showNotification(
        "error",
        "Error",
        getLocalizedMessage(intl, "productCategory.productCategoryRequired")
      );
    } else if (!values.subCategory[0].name) {
      showNotification(
        "error",
        "Error",
        getLocalizedMessage(intl, "productCategory.requiredSubProductCategory")
      );
    } else {
      handleSubmit();
    }
  };

  const handleCancel = () => {
    resetForm();
    navigate("/product-category");
  };

  return (
    <ThreeRow
      style={AddProductCategoryFormStyle.mainThreeRowStyle}
      topSection={
        <CustomInput
          size={"small"}
          required
          className={Style["Product-category-name"]}
          label={getLocalizedMessage(
            intl,
            "productCategory.productCategoryName"
          )}
          placeholder={getLocalizedMessage(
            intl,
            "productCategory.productCategoryName.placeholder"
          )}
          name={AddProductCategoryFormFieldName.NAME}
          value={values[AddProductCategoryFormFieldName.NAME]}
          onChange={handleChange}
          error={errors[AddProductCategoryFormFieldName.NAME]}
        />
      }
      topSectionStyle={AddProductCategoryFormStyle.topSectionStyle}
      middleSectionStyle={AddProductCategoryFormStyle.middleSectionStyle}
      middleSection={
        <AddSubCategoryFormContainer
          removeSubProductCategory={removeSubProductCategory}
          addSubProductCategory={addSubProductCategory}
          values={values}
          handleSubProductCategoryChange={handleSubProductCategoryChange}
          handleChange={handleChange}
          errors={errors}
          AddProductCategoryFormFieldName={AddProductCategoryFormFieldName}
          isEditMode={isEditMode}
        />
      }
      bottomSectionStyle={AddProductCategoryFormStyle.bottomSectionStyle}
      bottomSection={
        <Space size={0}>
          <Button
            className={Style["cancel-btn"]}
            ghost
            onClick={handleCancel}
            icon={null}
          >
            {getLocalizedMessage(intl, "button.text.cancel")}
          </Button>
          <Button
            onClick={submitAddProductCategory}
            className={Style["Add-Product-Category"]}
            icon={null}
            disabled={disabled}
          >
            {getLocalizedMessage(
              intl,
              isEditMode ? "button.save" : "button.text.addProductCategory"
            )}
          </Button>
        </Space>
      }
    />
  );
}
